// @flow

// PinVisual encapsulates the image or video portion of the PinRep. If the Pin
// is a Shop the Look Pin, it'll create the image collage. Controls and metadata
// will be layered over the top of this in PinRep.

import React, { Fragment } from 'react';
import Carousel from 'app/common/react/components/common/Carousel/Carousel';
import ClickthroughOrCloseupLink from 'app/common/react/components/PinRep/components/ClickthroughOrCloseupLink';
import CollageChips from 'app/common/react/components/PinRep/components/CollageChips';
import getPinTypes from 'app/common/react/components/PinRep/utils/getPinTypes';
import PinImage from 'app/common/react/components/PinRep/components/PinVisual/PinImage';
import PinVideoWithVisibility from 'app/common/react/components/PinRep/components/PinVisual/PinVideoWithVisibility';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { useTrackingParameters } from 'app/common/react/components/PinRep/TrackingParametersContext';
import { Box, Mask } from 'gestalt';

const PIN_IMAGE_MINIMUM_HEIGHT = 55;

// Build styles for cropping giraffe Pins based on Pin data.
export function getCropImageStyles({
  images,
  image_crop,
}: Pin): { marginBottom?: string, marginTop?: string } {
  const imageHeight = images && images['236x'].height;

  if (image_crop) {
    if (image_crop.min_y !== 0 || image_crop.max_y !== 1) {
      const { min_y, max_y } = image_crop;
      return {
        marginTop: `${min_y > 0 ? min_y * -1 * imageHeight : 0}px`,
        marginBottom: `${max_y < 1 ? Math.round((1 - max_y) * -1 * imageHeight) : 0}px`,
      };
    }
  }

  return {};
}

// PinVisual can force images to be a certain resolution/quality.
export type Resolution = 'default' | 'low-res' | 'high-res';

export type Props = {|
  pin: Pin,

  // Giraffe Pins can take up a ton of room in feeds, so we add crop ratios on
  // the backend and crop by default. Certain feeds may want to preserve the
  // full Pin image, so this prop allows explicit enabling/disabling.
  allowImageCropping: boolean,

  blockClickEvents?: boolean,

  // Crop pin image to a fixed size if it's a related product pin in closeup
  cropProductPinWidth?: number,

  // Is the user hovering over the Pin?
  isHovering: boolean,

  isSocialSeasonalBoard: boolean,

  // Callbacks, necessary for tracking.
  onError?: () => void,
  onLoad?: () => void,

  // Should we force image quality?
  resolution: Resolution,

  rootMargin?: string,

  showCarousel: boolean,
  // Should we show promoted videos (vs an image, experiment 'ads_desktop_video_ads')
  showPromotedVideos: boolean,
|};

export default function PinVisual({
  allowImageCropping,
  blockClickEvents,
  cropProductPinWidth,
  isHovering,
  isSocialSeasonalBoard = false,
  onError,
  onLoad,
  pin,
  resolution = 'default',
  rootMargin,
  showCarousel,
  showPromotedVideos,
}: Props) {
  const { slotIndex, viewParameter, viewType } = useTrackingParameters();

  // Pull relevant details out of pin.
  const {
    id: pinId = '',
    aggregated_pin_data: aggregatedPinData,
    image_signature: imageSignature,
  } = pin;
  const { pin_tags_chips: collageChips } = aggregatedPinData || {};

  const { isVideo, isPromoted, isGif } = getPinTypes(pin);

  // Based on the media type, show the right visual.
  let visual = null;

  if (isVideo && (showPromotedVideos || !isPromoted)) {
    visual = (
      <Box data-test-id="pinrep-video">
        <PinVideoWithVisibility
          rootMargin={rootMargin}
          inAdsDesktopVideoExperiment={isVideo && showPromotedVideos}
          pin={pin}
          onLoad={onLoad}
          viewType={viewType}
          viewParameter={viewParameter}
          highResOnly={resolution === 'high-res'}
          lowResOnly={resolution === 'low-res'}
          onError={onError}
          isExperimentalPinRep
        />
      </Box>
    );
  } else if (showCarousel) {
    visual = (
      <Box data-test-id="pinrep-carousel">
        <Carousel
          onError={onError}
          onLoad={onLoad}
          highResOnly={resolution === 'high-res'}
          lowResOnly={resolution === 'low-res'}
          pinId={pinId}
          showCarouselBadge={false}
          cropProductPinWidth={cropProductPinWidth}
        />
      </Box>
    );
  } else {
    const cropImageStyles = allowImageCropping ? getCropImageStyles(pin) : {};

    visual = (
      <Box
        dangerouslySetInlineStyle={{ __style: cropImageStyles }}
        data-test-id="pinrep-image"
        minHeight={PIN_IMAGE_MINIMUM_HEIGHT}
      >
        <PinImage
          /* $FlowFixMe - Pin types don't match between PinRep and PinImage. */
          pin={pin}
          highResOnly={resolution === 'high-res'}
          lowResOnly={resolution === 'low-res'}
          onLoad={onLoad}
          onError={onError}
          cropProductPinWidth={cropProductPinWidth}
          animateGif={isGif && isSocialSeasonalBoard}
        />
      </Box>
    );
  }

  const maskedVisual = <Mask wash>{visual}</Mask>;

  if (blockClickEvents) {
    return (
      <Fragment>
        {maskedVisual}

        {collageChips && (
          <Box dangerouslySetInlineStyle={{ __style: { marginTop: 1 } }}>
            <CollageChips pin={pin} enableCloseupLink />
          </Box>
        )}
      </Fragment>
    );
  }

  return (
    <ClickthroughOrCloseupLink
      pin={pin}
      contextLogData={{ grid_index: slotIndex, image_signature: imageSignature }}
      elementType={PCONST.thrift.ElementType.PIN_SOURCE_IMAGE}
    >
      {maskedVisual}

      {collageChips && (
        <Box dangerouslySetInlineStyle={{ __style: { marginTop: 1 } }}>
          <CollageChips pin={pin} enableCloseupLink={false} />
        </Box>
      )}
    </ClickthroughOrCloseupLink>
  );
}
